import { getEnvOverride } from './overrides';
import type { OAuth2Config } from '@thinkalpha/ui-types/types/oauth';
import type { KeycloakConfig } from '@thinkalpha/ui-types/types/schemas/OAuthConfigSchema';

declare global {
    const APP_COMMIT: string;
    const APP_CONFIG_NAME: string; // TODO: deprecate; use "environment" terminology
    const APP_VERSION: string;

    interface Window {
        appConfig: ConfigType;
        __APP_ENV__?: {
            APP_CONFIG_NAME: string;
            APP_COMMIT: string;
            APP_VERSION: string;
        };
    }
}

/**
 * Resolve config values from injected window.__APP_ENV__ (Docker/app-ws)
 * with fallback to build-time Vite globals (legacy per-env builds).
 * typeof guards prevent ReferenceError in contexts where Vite's define hasn't run.
 */
const runtimeEnv = typeof window !== 'undefined' ? window.__APP_ENV__ : undefined;

const resolvedConfigName =
    runtimeEnv?.APP_CONFIG_NAME ?? (typeof APP_CONFIG_NAME !== 'undefined' ? APP_CONFIG_NAME : 'local');
const resolvedCommit = runtimeEnv?.APP_COMMIT ?? (typeof APP_COMMIT !== 'undefined' ? APP_COMMIT : 'unknown');
const resolvedVersion = runtimeEnv?.APP_VERSION ?? (typeof APP_VERSION !== 'undefined' ? APP_VERSION : undefined);

/**
 * Sampling rate for production environments.
 */
const prodSampleRate = 10.0;

export interface ConfigType {
    accessApi: string;
    api: string;
    appVersion: string | undefined;
    commit: string;
    documentTitle: string;
    nlpApi: string;
    environmentName: string;
    i18nVersion: string;
    gaussApi: string;
    tradingApi: string;
    tracesSampleRate: number;
    backtestSettingsSchemaVersion: number;
    bardexApi: string;
    /**
     * URI of the legacy locates deployment. When specified, UI uses it instead of platform-ws.
     */
    legacyLocatesApi?: string;
    locatesApi?: string;
    /**
     * OAuth2 config
     */
    oAuth2Config: OAuth2Config;
    tableProxy: string;
    httpNgTableProxy: string;
    wsNgTableProxy: string;
    /**
     * Launch Darkly Config
     */
    launchDarklyClientSideId: string;
    newQuantApi: string;
    /**
     * Stripe publishable key for payment elements
     */
    stripePublishableKey: string;
}

/**
 *
 * @param env the name of the environment; must be a valid API environment
 * @returns partial configuration object
 */
const shared = (env: string) =>
    ({
        /**
         * APIS (env-driven)
         */
        accessApi: `https://access-ws.${env}.thinkalpha.io`,
        api: `https://api.${env}.thinkalpha.io`,
        documentTitle: 'DEV alphaLENS',
        gaussApi: `https://gauss-ws.${env}.thinkalpha.io`,
        tradingApi: `https://trading-ws.${env}.thinkalpha.io`,
        nlpApi: `https://nlp.${env}.thinkalpha.io`,
        bardexApi: `https://bardex.${env}.thinkalpha.io`,
        environmentName: `${env}`,
        commit: resolvedCommit,
        /**
         * VARS (base, expect overrides)
         */
        tracesSampleRate: 1.0,
        appVersion: undefined,
        backtestSettingsSchemaVersion: 1,
        locatesApi: `https://locates-ws.${env}.thinkalpha.io`,
        legacyLocatesApi: undefined, // only available in QA and above,
        tableProxy: `https://table-ws.${env}.thinkalpha.io`,
        httpNgTableProxy: `https://table.${env}.thinkalpha.io`,
        wsNgTableProxy: `wss://table-socket.${env}.thinkalpha.io`,
        launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
        newQuantApi: `https://quant-ws.${env}.thinkalpha.io`,
    }) satisfies Partial<ConfigType>;

type EnvStr = 'prod' | 'prod-alpha' | 'dev' | 'qa' | 'uat';

const makeKeycloakConfig = ({
    keycloakURL,
    tenantID,
    applicationID,
}: {
    keycloakURL: string;
    tenantID: string;
    applicationID: string;
}): KeycloakConfig => {
    let applicationURL;
    if (APP_ELECTRON) {
        applicationURL = 'https://dev.thinkalpha.io';
    } else if (typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope) {
        applicationURL = self.location.origin;
    } else {
        applicationURL = window.location.origin;
    }
    return {
        type: 'keycloak',
        keycloakURL,
        userInfoURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/userinfo`,
        logoutURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/logout`,
        tokenRevocationURL: `${keycloakURL}/${tenantID}/protocol/openid-connect/revoke`,
        tenantID,
        applicationID,
        callbackURL: `${applicationURL}/oauth2/callback`,
        loggedOutURL: `${applicationURL}/loggedout`,
    };
};

const makeDevOAuth2Config: () => KeycloakConfig = () =>
    makeKeycloakConfig({
        keycloakURL: 'https://auth.dev.thinkalpha.io',
        tenantID: 'realms/thinkalpha',
        applicationID: 'platform-ui',
    });

export const getHrefEnv = (): EnvStr | undefined => {
    if (APP_ELECTRON) return undefined;

    // ToDo: Get askalpha spun out entirely from app config
    if (location.host === 'askalpha.io' || location.host === 'www.askalpha.io') {
        return 'prod';
    }

    if (location.host === 'lens.thinkalpha.io') return 'prod';
    if (location.host.endsWith('uat.thinkalpha.io')) return 'uat';
    if (location.host.endsWith('qa.thinkalpha.io')) return 'qa';
    if (location.host.endsWith('prod-alpha.thinkalpha.io')) return 'prod-alpha';
    if (location.host.endsWith('dev.thinkalpha.io')) return 'dev';
    if (location.host.endsWith('prod.thinkalpha.io')) return 'prod';
    if (location.host.endsWith('platform.thinkalpha.io')) return 'prod';

    return undefined;
};

/**
 * Check if we're running in a test environment
 */
const isTestEnvironment = (): boolean => {
    // Vitest sets either import.meta.env.VITEST or import.meta.vitest
    if (typeof import.meta !== 'undefined') {
        const m = import.meta as any;
        if (m.env?.VITEST || m.vitest) return true;
    }
    // Fallback for Node-based runners
    return typeof process !== 'undefined' && !!process.env?.VITEST;
};

let appConfig: ConfigType;
try {
    const envOverride = getEnvOverride();

    // if (envOverride) {
    //     logger.info({ message: `Overriding env config from \`${APP_CONFIG_NAME}\` to \`${envOverride}\`` });
    // }

    const ENV_TO_USE = envOverride || resolvedConfigName;

    if (ENV_TO_USE === 'prod') {
        appConfig = {
            ...shared('prod'),
            documentTitle: 'alphaLENS',
            tracesSampleRate: prodSampleRate,
            legacyLocatesApi: 'wss://locates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.prod.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            i18nVersion: 'prod',
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c87',
            stripePublishableKey:
                'pk_live_51SmgWTDvYDgvvI7uVFBHZKS0ThBT25qcdx3DWA5f1H8ejaKPA2buzH2nThgfgFlDsIB7D2XtAu1KcxzEx73S0Nby00wqJEhXzl',
        };
    } else if (ENV_TO_USE === 'dev') {
        appConfig = {
            ...shared('dev'),
            i18nVersion: 'latest',
            legacyLocatesApi: 'wss://locates-and-preborrows-app.dev.ny.thinkalpha.io',
            oAuth2Config: makeDevOAuth2Config(),
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    } else if (ENV_TO_USE === 'qa') {
        appConfig = {
            ...shared('qa'),
            i18nVersion: 'qa',
            documentTitle: 'QA alphaLENS',
            // legacyLocatesApi: 'wss://qalocates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.qa.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            launchDarklyClientSideId: '660c436987af650fef869be1',
            stripePublishableKey:
                'pk_test_51SrnwlDzho1vODWuQNXls9WKR2QIbSsci2zw00AbneA1sOCBYgxqlrQhNBzzM14CZE1sUL5po9idd53Mug4r6COs00l6iA4ViJ',
        };
    } else if (ENV_TO_USE === 'uat') {
        appConfig = {
            ...shared('uat'),
            i18nVersion: 'uat',
            documentTitle: 'UAT alphaLENS',
            tracesSampleRate: prodSampleRate / 2,
            legacyLocatesApi: 'wss://uatlocates.thinkalpha.io',
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'https://auth.uat.thinkalpha.io',
                tenantID: 'realms/thinkalpha',
                applicationID: 'platform-ui',
            }),
            launchDarklyClientSideId: '660c43746ca8d50f6f275d95',
            stripePublishableKey:
                'pk_test_51SrnxuDARSYIYP4k5Se8QbjI6vhbak1SmIcKsaGD9b3f29PUNMF9hI5oYc4h18EkpuK4CtaDMn15jl2HVC4LTrMg009cjF7byy',
        };
    } else {
        appConfig = {
            ...shared('dev'),
            i18nVersion: 'latest',
            appVersion: 'local',
            environmentName: 'local',
            launchDarklyClientSideId: '660c29bfb2e0e70fe6409c86',
            oAuth2Config: makeDevOAuth2Config(),
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    }

    if (import.meta.env.VITE_API_URL) {
        appConfig.api = import.meta.env.VITE_API_URL; // 'https://localhost:3000';
    }
    if (import.meta.env.VITE_TRADING_API_URL) {
        appConfig.tradingApi = import.meta.env.VITE_TRADING_API_URL; // 'https://localhost:3001';
    }
    if (import.meta.env.VITE_ACCESS_API_URL) {
        appConfig.accessApi = import.meta.env.VITE_ACCESS_API_URL; // 'https://localhost:3002';
    }
    if (import.meta.env.VITE_QUANT_API_URL) {
        appConfig.newQuantApi = import.meta.env.VITE_QUANT_API_URL; // 'https://localhost:3003';
    }
    if (import.meta.env.VITE_PROXY_URL) {
        appConfig.tableProxy = import.meta.env.VITE_PROXY_URL; // 'https://localhost:3005';
    }

    const env = getHrefEnv();

    if (env && env !== resolvedConfigName) {
        // TODO: Normalize href usage into overall configuration workflow
        // logger.info({
        //     message: `Using ${APP_CONFIG_NAME} config, but not on .${APP_CONFIG_NAME}. href; using "${env}" as current environment`,
        // });
        appConfig = {
            ...appConfig,
            ...shared(env),
        };
    }

    // Override all URLs with mock URLs when running in test environment
    if (isTestEnvironment()) {
        appConfig = {
            ...appConfig,
            // Avoid accidental network initializations in tests
            launchDarklyClientSideId: 'test',
            accessApi: 'http://localhost:9999/mock-access-api',
            api: 'http://localhost:9999/mock-api',
            gaussApi: 'http://localhost:9999/mock-gauss-api',
            tradingApi: 'http://localhost:9999/mock-trading-api',
            nlpApi: 'http://localhost:9999/mock-nlp-api',
            locatesApi: 'http://localhost:9999/mock-locates-api',
            legacyLocatesApi: 'ws://localhost:9999/mock-legacy-locates-api',
            tableProxy: 'http://localhost:9999/mock-table-proxy',
            newQuantApi: 'http://localhost:9999/mock-new-quant-api',
            environmentName: 'test',
            documentTitle: 'TEST alphaLENS',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
            // Use a mock OAuth config for tests
            oAuth2Config: makeKeycloakConfig({
                keycloakURL: 'http://localhost:9999/mock-auth',
                tenantID: 'test-realm',
                applicationID: 'test-platform-ui',
            }),
        };
    }
} catch (e) {
    if (e instanceof ReferenceError) {
        // logger.error({
        //     message: 'Failed to identify the app config name! Defaulting to dev.',
        // });
        appConfig = {
            ...shared('dev'),
            oAuth2Config: makeDevOAuth2Config(),
            i18nVersion: 'latest',
            stripePublishableKey:
                'pk_test_51SrmKHDS1eElTTUgwj0xKizbbFgXrzSaprR9OnWJo6PtnRQvzXvuDo6gGcrSQHAm6UyvI9ieddd15PGt2MzDsXvI00KlrQgYZq',
        };
    } else {
        throw e;
    }
}

appConfig.appVersion = resolvedVersion ?? appConfig.appVersion;

if (typeof window !== 'undefined') {
    window.appConfig = appConfig;
}

export { appConfig };
